import { useEffect, useMemo, useState } from "react";
import {
  Mail,
  Users,
  Search,
  Filter,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  ToggleLeft,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { exportNewsletterSubscribers } from "@/api/newsletter";
import { useNewsletterSubscribers } from "@/hooks/newsletter/useNewsletterSubscribers";
import {
  useToggleSubscriberStatus,
  useDeleteSubscriber,
} from "@/hooks/newsletter/useNewsletterSubscriberMutations";

const PAGE_SIZE = 25;
const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active only" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getFilenameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || null;
};

const downloadCsvBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function NewsletterSubscribers() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { mutate: toggleStatus, isPending: isToggling } = useToggleSubscriberStatus();
  const { mutate: deleteSubscriber, isPending: isDeleting } = useDeleteSubscriber();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [status]);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search || undefined,
      status,
    }),
    [page, search, status]
  );

  const { data, isLoading, isFetching, refetch } = useNewsletterSubscribers(queryParams);

  const subscribers = data?.data || [];
  const stats = data?.stats || { total: 0, active: 0, unsubscribed: 0 };
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };

  const activeInView = useMemo(
    () => subscribers.filter((subscriber) => subscriber.status === "active").length,
    [subscribers]
  );

  const selectedStatusLabel =
    STATUS_FILTER_OPTIONS.find((option) => option.value === status)?.label || "All statuses";

  const handleExportActive = async () => {
    try {
      setIsExporting(true);
      const { blob, contentDisposition } = await exportNewsletterSubscribers("active");

      const fallbackName = `newsletter-subscribers-active-${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = getFilenameFromDisposition(contentDisposition) || fallbackName;

      downloadCsvBlob(blob, filename);
      toast.success("CSV exported", {
        description: "Active subscribers were downloaded successfully.",
      });
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Failed to export CSV.";
      toast.error("Export failed", { description: message });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-400 space-y-6 p-4 sm:space-y-8 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
            Newsletter Subscribers
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base md:text-lg">
            Monitor total subscribers, active users, and export your active audience.
          </p>
        </div>

        <Button
          variant="outline"
          size="lg"
          onClick={handleExportActive}
          disabled={isExporting || isLoading}
          className="w-full sm:w-auto cursor-pointer">
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? "Exporting..." : "Export Active CSV"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Total
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Active
          </p>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{stats.active}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Unsubscribed
          </p>
          <p className="mt-2 text-3xl font-bold text-amber-600">{stats.unsubscribed}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Active In View
          </p>
          <p className="mt-2 text-3xl font-bold text-blue-600">{activeInView}</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm lg:flex-row lg:items-center">
        <div className="relative w-full lg:flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email or source..."
            className="h-11 w-full rounded-md border bg-white pr-4 pl-9 text-sm transition-colors outline-none focus:border-blue-500"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="lg"
              className="h-11 w-full justify-between border-dashed sm:w-auto sm:min-w-40 cursor-pointer">
              <div className="flex items-center">
                <Filter className="text-muted-foreground mr-2 h-4 w-4" />
                <span className="truncate">{selectedStatusLabel}</span>
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {STATUS_FILTER_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => setStatus(option.value)}
                className={status === option.value ? "bg-accent font-medium" : ""}>
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline"
          size="lg"
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-full lg:w-auto cursor-pointer">
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="rounded-xl border bg-white shadow-sm">
        <div className="hidden border-b px-6 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase md:grid md:grid-cols-[2fr_0.9fr_1.4fr_1.4fr_1.4fr_auto] md:gap-4">
          <span>Email</span>
          <span>Status</span>
          <span>Source</span>
          <span>Subscribed At</span>
          <span>Updated</span>
          <span></span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading subscribers...</div>
        ) : subscribers.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Mail className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-700">No subscribers found.</p>
            <p className="mt-1 text-sm text-gray-500">
              Try changing your filters or wait for new subscriptions.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {subscribers.map((subscriber) => (
              <div
                key={subscriber.id}
                className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[2fr_0.9fr_1.4fr_1.4fr_1.4fr_auto] md:items-center md:gap-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="truncate">{subscriber.email}</span>
                </div>

                <div>
                  <Badge
                    variant="secondary"
                    className={
                      subscriber.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }>
                    {subscriber.status}
                  </Badge>
                </div>

                <div className="text-sm text-gray-600">{subscriber.source || "-"}</div>

                <div className="text-sm text-gray-600">{formatDateTime(subscriber.createdAt)}</div>

                <div className="text-sm text-gray-600">{formatDateTime(subscriber.updatedAt)}</div>

                <div>
                  <Tooltip>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={isToggling}
                        onClick={() => toggleStatus(subscriber.id)}>
                        <ToggleLeft className="mr-2 h-4 w-4" />
                        Toggle status
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        disabled={isDeleting}
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setDeleteTarget(subscriber)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <TooltipContent side="top">Subscriber actions</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove subscriber?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-medium text-gray-900">{deleteTarget?.email}</span> from the
              newsletter list. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                if (deleteTarget) {
                  deleteSubscriber(deleteTarget.id, {
                    onSettled: () => setDeleteTarget(null),
                  });
                }
              }}>
              {isDeleting ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          Showing {subscribers.length} of {pagination.total} subscribers
        </p>

        <div className="flex items-center gap-2">
          <Button className="cursor-pointer"
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={pagination.page <= 1 || isFetching}>
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <span className="text-sm text-gray-600">
            Page {pagination.page} of {Math.max(1, pagination.totalPages)}
          </span>

          <Button className="cursor-pointer"
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={pagination.page >= Math.max(1, pagination.totalPages) || isFetching}>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
